import React, {useState} from 'react';
import {COLORS} from '@fupa/fupa-uikit';
import {object} from 'prop-types';
import ActivityHeader from 'app/components/stream/activities/activityHeader';
import styled from 'styled-components';
import {generateMatchDayTitle} from 'app/helpers/actionsHelpers';
import {Event, KeyboardArrowLeft, KeyboardArrowRight} from '@mui/icons-material';
import {MatchCardFooter} from 'app/components/matchCalendar/MatchCardFooter';
import {MatchStreamTeaser} from 'app/components/match/MatchStreamTeaser';
import {Button, MobileStepper} from '@mui/material';
import SwipeableViews from 'react-swipeable-views';
import {virtualize} from 'react-swipeable-views-utils';
import {useParams} from 'react-router';
const VirtualizeSwipeableViews = virtualize(SwipeableViews);

const HEADER_HEIGHT = 53;
const FOOTER_HEIGHT = 46;

const Container = styled.div`
  &&& {
    display: flex;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 0.5rem;

    ${props => props.theme.desktopLayout`
      overflow: auto;
      max-height: ${399 - HEADER_HEIGHT - FOOTER_HEIGHT}px;
      min-height: ${183 - HEADER_HEIGHT - FOOTER_HEIGHT}px;
  `}
  }
`;

const Wrapper = styled.div`
  ${props => props.theme.desktopLayout`
      max-height: inherit;
      min-height: inherit;
      overflow: hidden;
  `}
`;

const MatchDayResultActivity = ({activity}) => {
  const {matches} = activity.activity;
  const {districtSlug} = useParams();
  const elements = matches.map(match => <MatchStreamTeaser key={match.id} match={match} />);

  let title = activity.activity?.competition?.name ?? activity.activity.competitionName;
  const matchDayTitle = generateMatchDayTitle(activity.activity.round);
  if (matchDayTitle) {
    title += `, ${matchDayTitle}`;
  }

  let footerComp;
  if (activity.activity?.competition) {
    const competition = {
      active: activity.activity.season.active,
      ...activity.activity.competition,
      season: activity.activity.season,
    };

    const isLeague = activity.activity.round.type === 'league';

    footerComp = <MatchCardFooter competition={competition} isLeague={isLeague} />;
  }

  let groupByN = (n, data) => {
    let result = [];
    for (let i = 0; i < data.length; i += n) result.push(data.slice(i, i + n));
    return result;
  };

  const steps = elements.length > 8 ? groupByN(8, elements) : null;
  const content = steps ? <MatchDayStepper steps={steps} /> : <Container>{elements}</Container>;

  const regionNiederbayernSlugs = ['niederbayern', 'niederbayern-ost', 'niederbayern-west'];
  const isRegionInUrl = regionNiederbayernSlugs.includes(districtSlug);
  const sponsorCat = isRegionInUrl ? 'match_day_results' : ' ';
  return (
    <Wrapper>
      <ActivityHeader
        title='Spieltag'
        subtitle={title}
        icon={<Event style={{color: COLORS.white, fontSize: 16}} />}
        iconColor={COLORS.orangeSoda}
        timestamp={activity.timestamp}
        category={sponsorCat}
      />
      {content}
      {footerComp}
    </Wrapper>
  );
};

MatchDayResultActivity.propTypes = {
  activity: object.isRequired,
};

export default MatchDayResultActivity;

const MatchDayStepper = ({steps}) => {
  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = steps.length;

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleStepChange = step => {
    setActiveStep(step);
  };

  return (
    <>
      <VirtualizeSwipeableViews
        index={activeStep}
        onChangeIndex={handleStepChange}
        overscanSlideBefore={1}
        overscanSlideAfter={1}
        slideRenderer={({index, key}) => <Container key={key}>{steps[index]}</Container>}
      />
      <MobileStepper
        steps={maxSteps}
        position='static'
        activeStep={activeStep}
        nextButton={
          <Button size='small' onClick={handleNext} disabled={activeStep === maxSteps - 1}>
            <KeyboardArrowRight />
          </Button>
        }
        backButton={
          <Button size='small' onClick={handleBack} disabled={activeStep === 0}>
            <KeyboardArrowLeft />
          </Button>
        }
      />
    </>
  );
};
